import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowRight, FiCheck, FiPenTool, FiTarget, FiBook, FiRefreshCw } = FiIcons;

const BrandingService = () => {
  const services = [
    {
      icon: FiPenTool,
      title: 'Logo Design & Brand Identity',
      description: 'Create distinctive logos and visual identities that capture your brand essence and resonate with your target audience.'
    },
    {
      icon: FiTarget,
      title: 'Brand Strategy & Positioning',
      description: 'Develop comprehensive brand strategies that position your business effectively in the competitive South African market.'
    },
    {
      icon: FiBook,
      title: 'Brand Guidelines Development',
      description: 'Establish clear brand guidelines that ensure consistent application of your brand across all touchpoints.'
    },
    {
      icon: FiRefreshCw,
      title: 'Rebranding & Brand Refresh',
      description: 'Revitalize existing brands with fresh perspectives while maintaining brand equity and customer recognition.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Research',
      description: 'We dive deep into understanding your business, market, and audience to inform our creative strategy.',
      timeline: '1-2 weeks'
    },
    {
      step: '02',
      title: 'Strategy Development',
      description: 'Develop a comprehensive brand strategy that aligns with your business goals and market positioning.',
      timeline: '1 week'
    },
    {
      step: '03',
      title: 'Creative Concepts',
      description: 'Create multiple design concepts and present them with detailed explanations of design decisions.',
      timeline: '2-3 weeks'
    },
    {
      step: '04',
      title: 'Refinement & Finalization',
      description: 'Refine chosen concepts based on feedback and finalize all brand assets to perfection.',
      timeline: '1-2 weeks'
    },
    {
      step: '05',
      title: 'Brand Guidelines Delivery',
      description: 'Deliver comprehensive brand guidelines and provide training on proper brand implementation.',
      timeline: '1 week'
    }
  ];

  const packageFeatures = [
    'Complete brand audit and analysis',
    'Logo design with multiple variations',
    'Color palette and typography selection',
    'Brand messaging and tone of voice',
    'Business card and stationery design',
    'Brand guidelines document',
    'Social media profile templates',
    'Brand implementation support'
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6">
              Branding & <span className="text-purple-600">Identity</span>
            </h1>
            <p className="font-inter text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8">
              Create memorable brand identities that resonate with your target audience and establish a strong presence in the South African market.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-teal-600 transition-all duration-300"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              What We Offer
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive branding services that establish your unique identity and position your business for success.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="font-inter text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Our systematic approach ensures your brand identity perfectly captures your vision and resonates with your audience.
            </p>
          </motion.div>
          <div className="space-y-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className="flex-1">
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mr-4">
                        <span className="font-poppins font-bold text-white">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-xl text-gray-900">
                          {step.title}
                        </h3>
                        <p className="font-inter text-purple-600 font-medium text-sm">
                          {step.timeline}
                        </p>
                      </div>
                    </div>
                    <p className="font-inter text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Package & Pricing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6">
                Complete Branding Package
              </h2>
              <p className="font-inter text-lg text-gray-600 mb-8 leading-relaxed">
                Our comprehensive branding package includes everything you need to establish a strong, cohesive brand identity that stands out in the market.
              </p>
              <div className="space-y-3 mb-8">
                {packageFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="font-inter text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-purple-600 to-teal-600 rounded-xl p-6 text-white mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-inter text-sm opacity-90">Starting Investment</p>
                    <p className="font-poppins font-bold text-3xl">From R15,000</p>
                  </div>
                  <div className="text-right">
                    <p className="font-inter text-sm opacity-90">Timeline</p>
                    <p className="font-poppins font-semibold text-lg">6-8 weeks</p>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Get Started Today <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3">
                <img
                  src="https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=800&h=600&fit=crop"
                  alt="Branding design process"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white rounded-2xl shadow-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="font-poppins font-bold text-2xl text-purple-600">100+</div>
                  <div className="font-inter text-sm text-gray-600">Brands Created</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Build Your Brand?
            </h2>
            <p className="font-inter text-lg text-gray-300 max-w-2xl mx-auto mb-8">
              Let's create a brand identity that tells your story, connects with your audience, and drives business growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Brand Journey <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                See Our Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BrandingService;